import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import LeadModal from "../public/LeadModal";

// Order requested by user: About — Features — MFD — Private Investors — Pricing — Login — Connect With Us
const NAV_LINKS = [
  { to: "/about", label: "About" },
  { to: "/features", label: "Features" },
  { to: "/mfd", label: "MFD" },
  { to: "/private-investors", label: "Private Investors" },
  { to: "/pricing", label: "Pricing" },
];

export default function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dashboardHref =
    user?.role === "client" ? "/portfolio" : "/dashboard";

  return (
    <header
      data-testid="public-nav"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#07111F]/90 backdrop-blur-md border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="public-nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="text-white tracking-[0.18em] text-sm md:text-base font-medium">
            KINNTEGRA
          </span>
          <span className="hidden md:inline h-[6px] w-[6px] rounded-full bg-[#C8A96B] opacity-80 group-hover:opacity-100 transition" />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`public-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive
                    ? "text-white"
                    : "text-white/55 hover:text-white"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
          {isAuthenticated ? (
            <button
              onClick={() => navigate(dashboardHref)}
              data-testid="public-nav-go-dashboard"
              className="text-sm tracking-wide text-white/55 hover:text-white transition-colors"
            >
              Dashboard
            </button>
          ) : (
            <NavLink
              to="/login"
              data-testid="public-nav-login"
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-white" : "text-white/55 hover:text-white"
                }`
              }
            >
              Login
            </NavLink>
          )}
          <button
            onClick={() => setLeadOpen(true)}
            data-testid="public-nav-getstarted"
            className="text-sm text-[#07111F] font-medium px-5 py-2.5 rounded-full bg-[#C8A96B] hover:bg-[#d4b878] transition active:scale-[0.98]"
          >
            Connect With Us
          </button>
        </nav>

        <button
          className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#C8A96B]/40 bg-white/5 text-white hover:bg-white/10 transition active:scale-95"
          data-testid="public-nav-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden bg-[#07111F]/95 backdrop-blur-md border-t border-white/5"
          data-testid="public-nav-mobile-panel"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-white" : "text-white/70"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="border-t border-white/5 my-2" />
            {isAuthenticated ? (
              <button
                onClick={() => {
                  setOpen(false);
                  navigate(dashboardHref);
                }}
                className="text-base text-white text-left"
              >
                Go to Dashboard
              </button>
            ) : (
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="text-base text-white/80"
              >
                Login
              </Link>
            )}
            <button
              onClick={() => {
                setOpen(false);
                setLeadOpen(true);
              }}
              data-testid="public-nav-mobile-getstarted"
              className="text-center text-[#07111F] font-medium px-5 py-3 rounded-full bg-[#C8A96B]"
            >
              Connect With Us
            </button>
          </div>
        </div>
      )}
      {leadOpen && <LeadModal onClose={() => setLeadOpen(false)} source="public_nav" />}
    </header>
  );
}
